import { Fragment } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BrainCircuit,
  Command,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";

const workflowSteps = [
  {
    icon: Command,
    title: "Input",
    subtitle: "Prompt, trigger, or live data",
    description:
      "Capture prompts, forms, tickets, APIs, events, and files from the moments where work begins.",
    color: "#22D3EE",
    tags: ["Prompt intake", "Event sync"],
  },
  {
    icon: BrainCircuit,
    title: "AI Analysis",
    subtitle: "Understanding with context",
    description:
      "Models classify intent, reason over context, summarize content, and decide the best next automation move.",
    color: "#6366F1",
    tags: ["Intent parsing", "Decision scoring"],
  },
  {
    icon: Workflow,
    title: "Automation Engine",
    subtitle: "Execution across your stack",
    description:
      "Rules, approvals, AI agents, and integrations trigger in parallel so workflows move without manual chasing.",
    color: "#A78BFA",
    tags: ["Agent orchestration", "System actions"],
  },
  {
    icon: Zap,
    title: "Smart Output",
    subtitle: "Polished results in motion",
    description:
      "Ship dashboards, chatbot replies, escalations, alerts, synced records, and next-best actions instantly.",
    color: "#60A5FA",
    tags: ["AI responses", "Business outcomes"],
  },
];

const workflowMetrics = [
  { label: "Intent classification", value: "97.4%", color: "#22D3EE" },
  { label: "Average orchestration latency", value: "120ms", color: "#818CF8" },
  { label: "Workflows executed per day", value: "10.2K", color: "#A78BFA" },
];

const AutomationSection = () => {
  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_24%,rgba(34,211,238,0.08),transparent_24%),radial-gradient(circle_at_84%_68%,rgba(167,139,250,0.12),transparent_22%),linear-gradient(180deg,rgba(7,12,27,0.98),rgba(9,14,30,0.94))]" />
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none" />
      <div className="absolute right-[-10%] top-[12%] h-[460px] w-[460px] rounded-full bg-[radial-gradient(circle,rgba(96,165,250,0.16),transparent_68%)] blur-[130px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="ai-badge mb-5 inline-flex">
            <Sparkles className="h-3.5 w-3.5 text-ai-glow" />
            AI Automation Workflow
          </span>
          <h2
            className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            From <span className="gradient-text">input to smart output</span> in one
            futuristic pipeline
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-400">
            The workflow below maps the exact automation journey: Input -> AI Analysis
            -> Automation Engine -> Smart Output.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mt-16 overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(160deg,rgba(6,10,24,0.95),rgba(15,22,42,0.85))] p-6 shadow-[0_35px_120px_rgba(2,6,23,0.55)] sm:p-8 lg:p-10"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.1),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(167,139,250,0.16),transparent_32%)]" />
          <div className="absolute inset-0 scan-line opacity-25" />

          <div className="relative z-10">
            <div className="flex flex-col gap-4 border-b border-white/8 pb-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.35em] text-slate-500">
                  Workflow Visualizer
                </p>
                <h3
                  className="mt-3 text-2xl font-semibold text-white sm:text-[2.1rem]"
                  style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                >
                  Input -> AI Analysis -> Automation Engine -> Smart Output
                </h3>
              </div>

              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-medium tracking-[0.26em] text-emerald-300 uppercase">
                Autonomous runtime online
              </span>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-stretch">
              {workflowSteps.map((step, index) => (
                <Fragment key={step.title}>
                  <motion.div
                    whileHover={{ y: -8 }}
                    className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-slate-950/55 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                  >
                    <div
                      className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      style={{
                        background: `radial-gradient(circle at top, ${step.color}18, transparent 52%)`,
                      }}
                    />

                    <div className="relative z-10">
                      <div className="flex items-start justify-between gap-4">
                        <span
                          className="rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-[0.26em] uppercase"
                          style={{
                            color: step.color,
                            borderColor: `${step.color}40`,
                            background: `${step.color}10`,
                          }}
                        >
                          {step.subtitle}
                        </span>

                        <div
                          className="flex h-12 w-12 items-center justify-center rounded-2xl"
                          style={{
                            background: `${step.color}18`,
                            border: `1px solid ${step.color}35`,
                            boxShadow: `0 0 24px ${step.color}12`,
                          }}
                        >
                          <step.icon
                            className="h-5 w-5"
                            style={{
                              color: step.color,
                              filter: `drop-shadow(0 0 8px ${step.color})`,
                            }}
                          />
                        </div>
                      </div>

                      <h3
                        className="mt-8 text-2xl font-semibold text-white"
                        style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                      >
                        {step.title}
                      </h3>

                      <p className="mt-4 text-[0.95rem] leading-7 text-slate-400">
                        {step.description}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-2.5">
                        {step.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium tracking-[0.2em] text-slate-300 uppercase"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>

                  {index < workflowSteps.length - 1 && (
                    <div className="hidden items-center justify-center lg:flex">
                      <ArrowRight className="h-5 w-5 text-slate-500" />
                      <div className="relative ml-3 h-px w-[4.5rem] bg-gradient-to-r from-white/5 via-cyan-300/35 to-violet-400/35">
                        <motion.span
                          className="absolute -top-[7px] left-0 h-4 w-4 rounded-full"
                          style={{
                            background: step.color,
                            boxShadow: `0 0 16px ${step.color}`,
                          }}
                          animate={{ x: [0, 72, 0], opacity: [0, 1, 0] }}
                          transition={{
                            duration: 3.1,
                            repeat: Infinity,
                            ease: "linear",
                            delay: index * 0.35,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {workflowMetrics.map((metric, index) => (
                <motion.div
                  key={metric.label}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.45 }}
                  className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
                >
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">
                    {metric.label}
                  </p>
                  <p
                    className="mt-3 text-3xl font-bold text-white"
                    style={{ fontFamily: '"Space Grotesk", sans-serif', color: metric.color }}
                  >
                    {metric.value}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <div className="section-divider mt-16" />
    </section>
  );
};

export default AutomationSection;
